/**
 * Map `GET /spot/v1/me/orders/history` and `GET /spot/v1/me/trades` items to the Trade page
 * order-history / trade-history UI shapes (legacy socket-compatible fields).
 */

import {
  spotDecimalString,
  spotIdString,
  spotIsoNormalize,
} from "../Order/spotMeActivityHelpers";

export function normalizeSpotOrderHistoryApiItemForTrade(raw) {
  if (!raw || typeof raw !== "object") return null;

  const id =
    spotIdString(raw._id) ||
    (raw.id != null ? String(raw.id).trim() : "") ||
    (raw.client_order_id != null ? String(raw.client_order_id).trim() : "");
  if (!id) return null;

  const executions = Array.isArray(raw.executions) ? raw.executions.filter(Boolean) : [];
  const executed_prices =
    executions.length > 0
      ? executions.map((ex) => {
          const p = spotDecimalString(ex?.price ?? ex?.execution_price);
          const q = spotDecimalString(ex?.quantity ?? ex?.filled_quantity);
          const f = spotDecimalString(ex?.fee ?? "0");
          return {
            price: (p !== "" ? p : ex?.price) ?? ex?.execution_price,
            quantity: (q !== "" ? q : ex?.quantity) ?? ex?.filled_quantity,
            fee: (f !== "" ? f : ex?.fee) ?? "0",
            timestamp: ex?.timestamp ?? ex?.executed_at ?? ex?.created_at ?? null,
          };
        })
      : undefined;

  const side = typeof raw.side === "string" ? raw.side.trim().toUpperCase() : raw.side;

  return {
    ...raw,
    _id: id,
    side,
    type: raw.type ?? raw.order_type,
    order_type: raw.order_type ?? raw.type,
    user_status: raw.user_status ?? raw.status,
    status: raw.status,
    created_at: raw.created_at,
    updated_at: raw.updated_at ?? raw.updatedAt,
    quantity: raw.quantity,
    remaining_quantity: raw.remaining_quantity ?? raw.remaining,
    filled_quantity: raw.filled_quantity ?? raw.filled,
    avg_execution_price: raw.avg_execution_price,
    executed_value: raw.executed_value,
    price: raw.price,
    time_in_force: raw.time_in_force,
    reject_reason: raw.reject_reason ?? raw.rejectReason,
    fill_percent: (() => {
      const qty = parseFloat(raw.quantity);
      const filled = parseFloat(raw.filled_quantity ?? raw.filled);
      if (qty > 0 && !isNaN(filled)) return `${Math.min(100, (filled / qty) * 100).toFixed(2)}%`;
      return raw.fill_percent ?? raw.fillPercent ?? null;
    })(),
    executed_prices,
    pair: raw.pair,
    ask_currency: raw.ask_currency,
    pay_currency: raw.pay_currency,
    total_fee: raw.total_fee,
    total_tds: raw.total_tds,
  };
}

export function normalizeSpotOrdersHistoryItemsForTrade(items) {
  const list = Array.isArray(items) ? items : [];
  return list.map(normalizeSpotOrderHistoryApiItemForTrade).filter(Boolean);
}

export function normalizeSpotTradeFillForTrade(raw) {
  if (!raw || typeof raw !== "object") return null;
  const id =
    spotIdString(raw._id) ||
    (raw.trade_id != null ? `${spotIdString(raw.trade_id)}:${String(raw.side ?? "").trim()}` : "");

  const sideRaw = typeof raw.side === "string" ? raw.side.trim().toUpperCase() : String(raw.side ?? "");
  const idFinal = id || `${sideRaw}:${spotIsoNormalize(raw.executed_at)}:${raw.pair ?? ""}`;

  const executedAtIso = spotIsoNormalize(raw.executed_at) || raw.executed_at;

  return {
    ...raw,
    _id: idFinal,
    side: sideRaw === "BUY" || sideRaw === "SELL" ? sideRaw : raw.side,
    pair: raw.pair != null ? String(raw.pair).trim().toUpperCase() : raw.pair,
    executed_at: executedAtIso,
    price: spotDecimalString(raw.price) !== "" ? spotDecimalString(raw.price) : raw.price,
    quantity: spotDecimalString(raw.quantity) !== "" ? spotDecimalString(raw.quantity) : raw.quantity,
    quote_quantity:
      spotDecimalString(raw.quote_quantity) !== "" ? spotDecimalString(raw.quote_quantity) : raw.quote_quantity,
    fee: spotDecimalString(raw.fee) !== "" ? spotDecimalString(raw.fee) : raw.fee,
    fee_asset: raw.fee_asset,
    tds: spotDecimalString(raw.tds) !== "" ? spotDecimalString(raw.tds) : raw.tds,
    tds_asset: raw.tds_asset,
    is_maker: typeof raw.is_maker === "boolean" ? raw.is_maker : raw.isMaker,
    quote_asset: raw.quote_asset,
    order_id: raw.order_id ?? raw.orderId,
  };
}

export function normalizeSpotTradeFillsItemsForTrade(items) {
  const list = Array.isArray(items) ? items : [];
  return list.map(normalizeSpotTradeFillForTrade).filter(Boolean);
}

/** Dedupe by `_id` keeping first occurrence (stable order preserved by caller). */
export function dedupeSpotHistoryById(rows, keyOf = (r) => r?._id ?? r?.id ?? "") {
  const seen = new Set();
  const out = [];
  for (const r of rows || []) {
    const k = String(keyOf(r) || "");
    if (!k || seen.has(k)) continue;
    seen.add(k);
    out.push(r);
  }
  return out;
}
